// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Convenience accessors over the generic tree (spec §6.1 "Helpers").
 *
 * All of these hold *references* into the parse tree — nothing copies data out.
 * The merge engine depends on that so it can splice original nodes and preserve
 * unknown children (spec §6.2).
 */

import { atom, isAtom, isNode, node, type Atom, type Node, type SexpChild } from './types.ts';

export type Path = string | readonly string[];

function pathParts(path: Path): readonly string[] {
  return typeof path === 'string' ? path.split('.') : path;
}

/** First child node named `name`, or undefined. */
export function get(n: Node, name: string): Node | undefined {
  for (const c of n.children) if (isNode(c) && c.name === name) return c;
  return undefined;
}

/** All child nodes named `name`. */
export function getAll(n: Node, name: string): Node[] {
  const out: Node[] = [];
  for (const c of n.children) if (isNode(c) && c.name === name) out.push(c);
  return out;
}

/** Resolve a dotted path of node names, e.g. `setup.stackup`. */
export function getPath(n: Node, path: Path): Node | undefined {
  let cur: Node | undefined = n;
  for (const part of pathParts(path)) {
    if (cur === undefined) return undefined;
    cur = get(cur, part);
  }
  return cur;
}

/** Positional atom children of a node, in order. Child *nodes* are skipped. */
export function atoms(n: Node): Atom[] {
  return n.children.filter(isAtom);
}

/** `i`-th atom child of the node at `path`, as a string. */
export function getStr(n: Node, path: Path, index = 0): string | undefined {
  const target = getPath(n, path);
  if (target === undefined) return undefined;
  return atoms(target)[index]?.value;
}

/** `i`-th atom child of the node at `path`, parsed as a float. */
export function getNum(n: Node, path: Path, index = 0): number | undefined {
  const s = getStr(n, path, index);
  if (s === undefined) return undefined;
  const v = Number(s);
  return Number.isFinite(v) ? v : undefined;
}

/** All atom children of the node at `path`, parsed as floats. */
export function getNums(n: Node, path: Path): number[] {
  const target = getPath(n, path);
  if (target === undefined) return [];
  const out: number[] = [];
  for (const a of atoms(target)) {
    const v = Number(a.value);
    if (Number.isFinite(v)) out.push(v);
  }
  return out;
}

/** All atom children of the node at `path`, as strings. */
export function getStrs(n: Node, path: Path): string[] {
  const target = getPath(n, path);
  if (target === undefined) return [];
  return atoms(target).map((a) => a.value);
}

/**
 * Replace (or create) a single-atom child node: `setChild(n, 'width', '0.25')`
 * yields `(width 0.25)`. Existing extra children of that node are discarded.
 */
export function setChild(n: Node, name: string, value: string | number, quoted?: boolean): Node {
  const v = typeof value === 'number' ? String(value) : value;
  const existing = get(n, name);
  if (existing) {
    existing.children = [atom(v, quoted)];
    return existing;
  }
  const created = node(name, [atom(v, quoted)]);
  n.children.push(created);
  return created;
}

/** Remove every child node named `name`. Returns how many were removed. */
export function removeChildren(n: Node, name: string): number {
  const before = n.children.length;
  n.children = n.children.filter((c) => !(isNode(c) && c.name === name));
  return before - n.children.length;
}

/**
 * KiCad 6 wrote `(tstamp "…")`; KiCad 7+ writes `(uuid "…")`. Both mean the same
 * thing (spec §3.2), so every read goes through this.
 */
export function getUuid(n: Node): string | undefined {
  return getStr(n, 'uuid') ?? getStr(n, 'tstamp');
}

export function setUuid(n: Node, value: string): void {
  if (get(n, 'uuid')) setChild(n, 'uuid', value, true);
  else if (get(n, 'tstamp')) setChild(n, 'tstamp', value, true);
  else setChild(n, 'uuid', value, true);
}

/** Depth-first walk over every node in the subtree, including `n` itself. */
export function* walk(n: Node): Generator<Node> {
  yield n;
  for (const c of n.children) {
    if (isNode(c)) yield* walk(c);
  }
}

/**
 * Depth-first walk yielding each node together with its dotted path from the
 * root, e.g. `kicad_pcb.setup.stackup`. Used by the coverage scanner.
 */
export function* walkWithPath(n: Node, prefix = ''): Generator<{ node: Node; path: string }> {
  const path = prefix === '' ? n.name : `${prefix}.${n.name}`;
  yield { node: n, path };
  for (const c of n.children) {
    if (isNode(c)) yield* walkWithPath(c, path);
  }
}

/** Index of the first child that is `target` (identity), or -1. */
export function indexOfChild(parent: Node, target: SexpChild): number {
  return parent.children.indexOf(target);
}
